// Story 153 (CU8) -- presentation helpers shared by the load-and-capacity block, its chart, its
// summary strip and its group cards. Nothing here is a business rule (RN44): every figure comes from
// the `loadCapacity` response and is only formatted, clamped for display or added up over the columns
// the user chose to look at.

// RN15/RN16 -- BACKLOG/OVERDUE are the only special columns; they never carry capacity.
export const isSpecialBucket = (bucket) => bucket.periodType === 'BACKLOG' || bucket.periodType === 'OVERDUE'

export const SPECIAL_LABEL_KEY = { BACKLOG: 'Backlog', OVERDUE: 'Overdue' }

// Hours and percentages are painted with at most one decimal, exactly as Sun rounds them (§1/§2 of
// the contract); thousands are grouped by the UI language (so 14040 reads "14.040" in Spanish), never
// by the OS locale of the machine, which is what `Intl` falls back to without an explicit tag.
const FALLBACK_LOCALE = 'en'
const formatters = new Map()

const numberFormatter = (locale) => {
  const tag = locale || FALLBACK_LOCALE
  if (!formatters.has(tag)) {
    let formatter
    try {
      formatter = new Intl.NumberFormat(tag, { maximumFractionDigits: 1 })
    } catch (unknownTag) {
      formatter = new Intl.NumberFormat(FALLBACK_LOCALE, { maximumFractionDigits: 1 })
    }
    formatters.set(tag, formatter)
  }
  return formatters.get(tag)
}

export const formatHours = (value, locale) => numberFormatter(locale).format(Number(value) || 0)

export const formatPercent = (value, locale) => numberFormatter(locale).format(Number(value) || 0)

// The range buttons: how many regular columns are shown at once. The special column (BACKLOG or
// OVERDUE), when present, is always kept in front of them (RN15/RN16).
export const RANGE_OPTIONS = { WEEK: [13, 26, 52], MONTH: [3, 6, 12] }

export const visibleBuckets = (buckets = [], range = null) => {
  if (range === null || range === undefined) {
    return buckets
  }
  const special = buckets.filter(isSpecialBucket)
  const regular = buckets.filter((bucket) => !isSpecialBucket(bucket)).slice(0, range)
  return [...special, ...regular]
}

// Totals of the visible columns, added up from the server's own per-column figures: load, OEE
// capacity, what is still free (never negative per column, RN22), the excess of the saturated
// columns (RN28, `excessHours` as it arrived), the OEE loss (calendar minus OEE capacity) and the
// load of the special column. The occupancy of the range is the only ratio formed here, and only
// because the contract has no figure for "the 13 weeks the user is looking at".
export const summarizeBuckets = (buckets = []) => {
  const regular = buckets.filter((bucket) => !isSpecialBucket(bucket))
  const special = buckets.find(isSpecialBucket)
  const totals = regular.reduce((acc, bucket) => {
    acc.loadHours += bucket.totalLoadHours
    acc.oeeCapacityHours += bucket.oeeCapacityHours
    acc.calendarCapacityHours += bucket.calendarCapacityHours
    acc.availableHours += Math.max(bucket.availableCapacityHours, 0)
    acc.excessHours += bucket.saturated ? bucket.excessHours : 0
    acc.oeeLossHours += Math.max(bucket.calendarCapacityHours - bucket.oeeCapacityHours, 0)
    acc.saturatedPeriods += bucket.saturated ? 1 : 0
    return acc
  }, { loadHours: 0, oeeCapacityHours: 0, calendarCapacityHours: 0, availableHours: 0, excessHours: 0, oeeLossHours: 0, saturatedPeriods: 0 })
  return {
    ...totals,
    periodCount: regular.length,
    occupancyPercent: totals.oeeCapacityHours > 0 ? (totals.loadHours / totals.oeeCapacityHours) * 100 : null,
    specialType: special ? special.periodType : null,
    specialLoadHours: special ? special.totalLoadHours : 0,
    // Shares for the captions of the strip: what the OEE leaves of the calendar, what is still free
    // or in excess of the OEE capacity. All of them from the same sums, none from the client's own
    // reading of the schedule.
    oeePercentOfCalendar: totals.calendarCapacityHours > 0 ? (totals.oeeCapacityHours / totals.calendarCapacityHours) * 100 : null,
    availablePercentOfCapacity: totals.oeeCapacityHours > 0 ? (totals.availableHours / totals.oeeCapacityHours) * 100 : null,
    excessPercentOfCapacity: totals.oeeCapacityHours > 0 ? (totals.excessHours / totals.oeeCapacityHours) * 100 : null
  }
}

// Whole percentages for the captions and the in-column labels: "31 %", never "31.4 %".
export const formatWholePercent = (value, locale) => formatPercent(Math.round(Number(value) || 0), locale)

// Display thresholds of the occupancy figure of the strip: the same colours the ribbon uses for its
// occupancy card (RN38): red at or above saturation, amber when it gets close.
export const occupancyTone = (percent) => {
  if (percent === null || percent === undefined) {
    return 'neutral'
  }
  if (percent >= 100) {
    return 'critical'
  }
  if (percent >= 85) {
    return 'warning'
  }
  return 'ok'
}

// The palette colour of each `occupancyTone`: one map for every figure coloured by it.
export const OCCUPANCY_TONE_COLOR = { critical: 'error.main', warning: 'warning.main', ok: 'success.main', neutral: 'text.primary' }

// Story 175 (machine detail drawer) -- its hours and percentages always carry ONE decimal ("262,0 h",
// "105,0 %"), in the UI language, and a negative figure wears the typographic minus ("−12,4 h"), the
// same sign the translated literals use ("OEE −22,0 %"). Formatting only: the figure is Sun's (INV-I).
const TYPOGRAPHIC_MINUS = '−'
const oneDecimalFormatters = new Map()

const oneDecimalFormatter = (locale) => {
  const tag = locale || FALLBACK_LOCALE
  if (!oneDecimalFormatters.has(tag)) {
    let formatter
    try {
      formatter = new Intl.NumberFormat(tag, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    } catch (unknownTag) {
      formatter = new Intl.NumberFormat(FALLBACK_LOCALE, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    }
    oneDecimalFormatters.set(tag, formatter)
  }
  return oneDecimalFormatters.get(tag)
}

export const formatOneDecimal = (value, locale) => oneDecimalFormatter(locale).format(Number(value) || 0).replace('-', TYPOGRAPHIC_MINUS)

// "+21", "−9", "0": the deviation in days of a fabrication order, as Sun sent it.
export const formatSignedDays = (days) => {
  if (days > 0) {
    return `+${days}`
  }
  if (days < 0) {
    return `${TYPOGRAPHIC_MINUS}${Math.abs(days)}`
  }
  return '0'
}

// "2026-09-21" -> "21/09": a string rearrangement of the day the server sent, not a date computation.
export const dayMonthOf = (isoDate) => {
  if (typeof isoDate !== 'string' || isoDate.length < 10) {
    return ''
  }
  return `${isoDate.slice(8, 10)}/${isoDate.slice(5, 7)}`
}

// "2026-W35" -> 35: the week number Sun already wrote into its period key (RN28: Jupiter never derives
// a week from a date). Anything that is not a weekly key has no week number.
export const weekNumberOfPeriodKey = (periodKey) => {
  const match = /^\d{4}-W(\d{1,2})$/.exec(typeof periodKey === 'string' ? periodKey : '')
  return match ? Number(match[1]) : null
}
